import cn from "classnames";
import { Transition } from "components/shared/column";
import { Checkbox, Field } from "components/shared/form";
import Loader from "components/shared/loader";
import { validateForm } from "helpers/form";
import { paths } from "helpers/paths";
import { viewContainer } from "helpers/redux";
import { generateIntegrationClientId, generateIntegrationClientSecret } from "helpers/secureRandom";
import { requiredField } from "helpers/validators";
import React, { FormEvent, useEffect, useRef, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import { alert } from "services/alert";
import {
    applyIntegrationClient,
    getIntegrationClient,
    IntegrationClientEditModel,
} from "sources/admin/integrationClients";
import OrganizationAccess from "./organizations";
import SecretModal from "./secretModal";

interface FormData extends IntegrationClientEditModel {
    secret: string;
}

interface IntegrationClientFormProps extends RouteComponentProps<{ clientId?: string }> {
    modal: (params: {}) => void;
    addModal: (params: {}) => void;
}

const initialData: FormData = { clientId: "", name: "", secret: "", isActive: true };

const config = {
    name: {
        label: "Название",
        required: true,
        inputProps: { name: "name", autoComplete: "off" },
        validators: [requiredField],
    },
    clientId: {
        label: "Client ID",
        required: true,
        inputProps: { name: "clientId", autoComplete: "off" },
        validators: [requiredField],
    },
    secret: {
        label: "Client Secret",
        inputProps: { name: "secret", type: "password", autoComplete: "new-password" },
    },
};

function IntegrationClientForm({ history, match, modal, addModal }: IntegrationClientFormProps) {
    const clientId = match.params.clientId ? decodeURIComponent(match.params.clientId) : undefined;
    const isNew = !clientId;
    const [data, setData] = useState<FormData>(initialData);
    const initialIsActive = useRef(true);
    const [loading, setLoading] = useState(!isNew);

    useEffect(() => {
        if (!clientId) {
            return;
        }

        getIntegrationClient(clientId).then((result) => {
            if (!result) {
                alert({
                    type: "error",
                    title: "Ошибка",
                    message: "Интеграционный клиент не найден.",
                });
                history.push(paths.integrationClients);
                return;
            }

            setData({ ...result, secret: "" });
            initialIsActive.current = result.isActive;
            setLoading(false);
        });
    }, [clientId, history]);

    const updateData = ({ name, value }: { name: keyof FormData; value: string | boolean }) => {
        setData((current) => ({ ...current, [name]: value }));
    };

    const normalizedData = () => ({
        clientId: data.clientId.trim(),
        name: data.name.trim(),
        isActive: data.isActive,
    });

    const validate = () => {
        return validateForm(config, normalizedData());
    };

    const save = (blockConfirmed = false) => {
        if (!validate()) {
            return Promise.resolve(false);
        }

        if (!isNew && initialIsActive.current && !data.isActive && !blockConfirmed) {
            modal({
                name: "confirm",
                title: "Заблокировать интеграционного клиента?",
                text: "Клиент не сможет получать новые токены. Ранее выданные токены также перестанут приниматься.",
                buttons: { confirm: "Заблокировать", cancel: "Отмена" },
                action: () => save(true),
            });
            return Promise.resolve(false);
        }

        setLoading(true);
        const model = normalizedData();
        const submittedSecret = isNew && data.secret.trim() ? data.secret.trim() : null;
        const applyModel = () =>
            applyIntegrationClient({ ...model, secret: submittedSecret }).then((result) => {
                if (!result) {
                    setLoading(false);
                    return false;
                }

                if (isNew) {
                    const createdClient = typeof result === "object" ? result : null;
                    setData((current) => ({ ...current, secret: "" }));
                    setLoading(false);
                    if (!createdClient?.secret || !createdClient?.clientId) {
                        history.push(paths.integrationClients);
                        return false;
                    }
                    const createdClientPath = paths.integrationClient(encodeURIComponent(createdClient.clientId));
                    modal({
                        name: "default",
                        title: "Интеграционный клиент создан",
                        windowMod: "wide",
                        CustomBody: SecretModal,
                        bodyData: {
                            clientId: createdClient.clientId,
                            clientName: createdClient.name,
                            secret: createdClient.secret,
                        },
                        withoutEscClose: true,
                        withoutCloseButton: true,
                        onCloseAction: () => {
                            history.push(createdClientPath);
                        },
                    });
                    return true;
                }

                initialIsActive.current = model.isActive;
                setData((current) => ({ ...current, ...model, secret: "" }));
                setLoading(false);
                alert({ type: "success", title: "Сохранено", message: "Изменения сохранены." });
                return true;
            });

        return applyModel();
    };

    const rotateSecret = () => {
        modal({
            name: "confirm",
            title: "Заменить Client Secret?",
            text: "После замены старый Client Secret нельзя будет использовать для получения новых токенов. Уже выданные JWT могут работать до истечения срока; для экстренного отзыва сначала заблокируйте клиента.",
            buttons: { confirm: "Заменить", cancel: "Отмена" },
            action: () => {
                if (!data.clientId) {
                    return false;
                }

                let secret: string;
                try {
                    secret = generateIntegrationClientSecret();
                } catch (_error) {
                    alert({
                        type: "error",
                        title: "Ошибка",
                        message: "Браузер не поддерживает безопасную генерацию секрета.",
                    });
                    return false;
                }

                setLoading(true);
                const currentClient: IntegrationClientEditModel = {
                    clientId: data.clientId,
                    name: data.name,
                    isActive: data.isActive,
                };
                return applyIntegrationClient({ ...currentClient, secret }).then((result) => {
                    setLoading(false);
                    if (!result) {
                        return false;
                    }
                    return { ...currentClient, secret };
                });
            },
            onSuccessAction: (created?: (IntegrationClientEditModel & { secret: string }) | false) => {
                if (!created || !created.clientId || !created.name || !created.secret) {
                    return;
                }
                addModal({
                    name: "default",
                    title: "Client Secret заменён",
                    windowMod: "wide",
                    CustomBody: SecretModal,
                    bodyData: {
                        clientId: created.clientId,
                        clientName: created.name,
                        secret: created.secret,
                    },
                    withoutEscClose: true,
                    withoutCloseButton: true,
                });
            },
        });
    };

    const generateClientId = () => {
        try {
            updateData({ name: "clientId", value: generateIntegrationClientId() });
        } catch (_error) {
            alert({
                type: "error",
                title: "Ошибка",
                message: "Браузер не поддерживает безопасную генерацию Client ID.",
            });
        }
    };

    const copyClientId = () => {
        if (!navigator.clipboard?.writeText || !data.clientId) {
            return;
        }
        navigator.clipboard.writeText(data.clientId).then(
            () => alert({ type: "success", title: "Скопировано", message: "Client ID скопирован." }),
            () => alert({ type: "error", title: "Ошибка", message: "Не удалось скопировать Client ID." }),
        );
    };

    const submit = (event: FormEvent) => {
        event.preventDefault();
        save();
    };

    return (
        <Transition dir="full">
            <div className="page-book__show book-show">
                <div className="book-show__head">
                    <button
                        type="button"
                        aria-label="Вернуться к списку"
                        className="book-show__close close close--sm"
                        onClick={() => history.push(paths.integrationClients)}
                    />
                    <div className="book-show__title">
                        {isNew ? "Создание интеграционного клиента" : "Интеграционный клиент"}
                    </div>
                </div>
                <div className={cn("book-show__body overflow-auto", { loading })}>
                    {loading && <Loader />}
                    <form className="container py-4" onSubmit={submit}>
                        <Field {...config.name} value={data.name} onChange={updateData} />
                        <Field
                            {...config.clientId}
                            inputProps={{ ...config.clientId.inputProps, disabled: !isNew }}
                            value={data.clientId}
                            onChange={updateData}
                            inputBottomText={
                                <div className="mt-2">
                                    {isNew && (
                                        <button
                                            type="button"
                                            className="btn btn-xs btn-outline-primary mr-2"
                                            onClick={generateClientId}
                                        >
                                            Сгенерировать
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-xs btn-outline-primary"
                                        disabled={!data.clientId}
                                        onClick={copyClientId}
                                    >
                                        Скопировать
                                    </button>
                                </div>
                            }
                        />
                        {isNew && (
                            <Field
                                {...config.secret}
                                value={data.secret}
                                onChange={updateData}
                                inputBottomText={
                                    <small className="form-text text-muted">
                                        Если оставить поле пустым, секрет будет создан автоматически. После сохранения
                                        его необходимо скопировать: повторно посмотреть секрет будет невозможно.
                                    </small>
                                }
                            />
                        )}
                        <Checkbox
                            label="Активен"
                            inputProps={{ name: "isActive" }}
                            value={data.isActive}
                            onChange={updateData}
                        />
                        <div className="d-flex justify-content-between mb-4">
                            {!isNew ? (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-secondary"
                                    disabled={loading}
                                    onClick={rotateSecret}
                                >
                                    Заменить Client Secret
                                </button>
                            ) : (
                                <div />
                            )}
                            <button type="submit" className="btn btn-sm btn-brand" disabled={loading}>
                                Сохранить
                            </button>
                        </div>
                        {!isNew && <OrganizationAccess clientId={data.clientId} modal={modal} />}
                    </form>
                </div>
            </div>
        </Transition>
    );
}

export default viewContainer(IntegrationClientForm, "integration-clients");
